/**
 * Style rules ST-01 ... ST-09 (W2-12a checker-runtime; DESIGN.md §7.6, §4.3, §5.1; rpe-editor-features.md E§3.16
 * AC-05 "Other" cautions and "Too Many Linear" events; formats-and-packaging.md P§2.7).
 *
 * Tips about the project rather than about Phira's behaviour: timing not confirmed (the wizard's Beat step was
 * skipped), the generated placeholder illustration, unused files, empty lines, long runs of straight segments,
 * RPE's "Other" cautions (off by default), MP3 / FLAC songs and incomplete chart details. Every problem says WHAT
 * and WHY and offers fixes: the style fixes of core/check/style.ts, W2-12b's media conversion fix, and navigation
 * through contracted commands. Ids are stable entity keys (core/check/geometry.ts `problemId`) that double as the
 * "Ignore here" key. ST-05 and ST-06 are Full-level rules: in Guided they run only when their `usesFeature` probe
 * finds the feature in the chart (`CheckRule.usesFeature`).
 *
 * Worker-safe: imports only src/core/**. Messages live in ./style.messages.ts (namespace 'checkerStyle').
 */

import { PLACEHOLDER_ILLUSTRATION_PATH } from '../../../core/check/context';
import {
  emitProblem,
  fixRef,
  fmtBeat,
  lineLabel,
  linesToCheck,
  msg,
  navFix,
  problemId,
  revealFix,
  type MessageParams,
} from '../../../core/check/geometry';
import {
  DIVISION_TAGS,
  LINEAR_RUN_MIN_SEGMENTS,
  LINEAR_RUN_PROPS,
  UNKNOWN_DETAIL,
  keysOfTrack,
  linearRuns,
  redundantLinearKeys,
  styleFixRef,
  workspaceFix,
} from '../../../core/check/style';
import type { CheckContext, CheckRule, FixRef } from '../../../core/check/types';
import type {
  AssetId,
  AssetRef,
  AssetRole,
  ChartMeta,
  LineId,
  LineProp,
  Note,
  TrackId,
  TrackValue,
} from '../../../core/model/types';
import { Beats } from '../../../core/time/beat';

const NS = 'checkerStyle';

function sm(key: string, params?: MessageParams): ReturnType<typeof msg> {
  return msg(`${NS}.${key}`, params);
}

function label(ctx: CheckContext, line: LineId): string {
  return lineLabel(ctx.doc, ctx.idx, line);
}

// ---------------------------------------------------------------------------------------------------------------
// ST-01 timing not confirmed
// ---------------------------------------------------------------------------------------------------------------

const st01: CheckRule = {
  id: 'ST-01',
  category: 'style',
  severity: 'tip',
  level: 'guided',
  scope: 'chart',
  deps: ['timing', 'tempo'],
  defaultEnabled: true,
  title: sm('st01Title'),
  check(ctx) {
    if (ctx.doc.timing.confirmed) return;
    const first = ctx.idx.bpmSorted()[0];
    const bpm = first === undefined ? null : (ctx.doc.bpm.get(first)?.bpm ?? null);
    emitProblem(ctx, {
      id: problemId('ST-01', 'chart', 'timing'),
      message: sm('st01What', {
        bpm: bpm === null ? '?' : String(Math.round(bpm * 1000) / 1000),
        offset: ctx.doc.timing.offsetMs,
      }),
      why: sm('st01Why'),
      at: {},
      fixes: [
        workspaceFix(sm('fixOpenTiming'), 'timing'),
        styleFixRef('problems.fix.confirmTiming', sm('fixConfirmTiming'), {}),
      ],
    });
  },
};

// ---------------------------------------------------------------------------------------------------------------
// ST-02 placeholder illustration
// ---------------------------------------------------------------------------------------------------------------

/** True when the chart has lines that came from an imported package (its files keep their package names). */
function hasImportedLines(ctx: CheckContext): boolean {
  for (const id of ctx.doc.lineOrder) if (ctx.doc.lines.get(id)?.origin === 'import') return true;
  return false;
}

const st02: CheckRule = {
  id: 'ST-02',
  category: 'style',
  severity: 'tip',
  level: 'guided',
  scope: 'chart',
  deps: ['meta', 'assets', 'lines'],
  defaultEnabled: true,
  title: sm('st02Title'),
  check(ctx) {
    const id = ctx.doc.meta.illustration;
    if (id === null) return;
    const asset = ctx.doc.assets.get(id);
    if (asset === undefined || asset.missing === true || asset.path !== PLACEHOLDER_ILLUSTRATION_PATH) return;
    if (hasImportedLines(ctx)) return;
    emitProblem(ctx, {
      id: problemId('ST-02', 'asset', id),
      message: sm('st02What'),
      why: sm('st02Why'),
      at: { asset: id, field: 'meta.illustration' },
      fixes: [navFix('assets.replace', sm('fixChoosePicture'), { asset: id })],
    });
  },
};

// ---------------------------------------------------------------------------------------------------------------
// ST-03 unused files
// ---------------------------------------------------------------------------------------------------------------

/** Roles a chart refers to (fonts and other files are carried along for their own sake). */
const REFERABLE_ROLES: ReadonlySet<AssetRole> = new Set([
  'music',
  'illustration',
  'texture',
  'gif',
  'hitsound',
  'shader',
  'video',
]);

function isUnused(ctx: CheckContext, a: AssetRef): boolean {
  if (!REFERABLE_ROLES.has(a.role)) return false;
  const u = ctx.idx.assetUsers(a.id);
  return u.lines.length + u.notes.length + u.effects.length + u.videos.length + u.meta.length === 0;
}

const st03: CheckRule = {
  id: 'ST-03',
  category: 'style',
  severity: 'tip',
  level: 'guided',
  scope: 'chart',
  deps: ['assets', 'lines', 'notes', 'effects', 'meta'],
  defaultEnabled: true,
  title: sm('st03Title'),
  check(ctx) {
    const unused = [...ctx.doc.assets.values()]
      .filter((a) => isUnused(ctx, a))
      .sort((a, b) => (a.path < b.path ? -1 : a.path > b.path ? 1 : 0));
    const all: AssetId[] = unused.map((a) => a.id);
    for (const a of unused) {
      const fixes: FixRef[] = [
        styleFixRef('problems.fix.removeAssets', sm('fixRemoveFile', { path: a.path }), { assets: [a.id] }),
      ];
      if (all.length > 1) {
        fixes.push(
          styleFixRef('problems.fix.removeAssets', sm('fixRemoveAllFiles', { count: all.length }), { assets: all }),
        );
      }
      emitProblem(ctx, {
        id: problemId('ST-03', 'asset', a.id),
        message: sm('st03What', { path: a.path }),
        why: sm('st03Why'),
        at: { asset: a.id, field: 'asset.path' },
        fixes,
      });
    }
  },
};

// ---------------------------------------------------------------------------------------------------------------
// ST-04 empty lines
// ---------------------------------------------------------------------------------------------------------------

/** Two track values are the same (numbers within rounding noise, colours and vectors component-wise). */
function sameValue(a: TrackValue, b: TrackValue): boolean {
  if (typeof a === 'number' || typeof b === 'number') {
    return typeof a === 'number' && typeof b === 'number' && Math.abs(a - b) <= 1e-9;
  }
  if (typeof a === 'string' || typeof b === 'string') return a === b;
  return a.length === b.length && a.every((x, i) => x === b[i]);
}

/** The one value a track holds for the whole chart, or undefined when it changes (a second value or a jump). */
function constantValue(ctx: CheckContext, track: TrackId): TrackValue | undefined {
  const keys = keysOfTrack(ctx.doc, ctx.idx, track);
  const first = keys[0];
  if (first === undefined) return undefined;
  const v = first.vOut;
  for (const k of keys) if (!sameValue(k.vIn, v) || !sameValue(k.vOut, v)) return undefined;
  return v;
}

/**
 * The sum of a numeric property's layers when every layer holds one value for the whole chart (0 without a track,
 * Phira's default), or null when the property changes.
 */
function constantSum(
  ctx: CheckContext,
  tracks: ReadonlyMap<LineProp, readonly TrackId[]>,
  prop: LineProp,
): number | null {
  let sum = 0;
  for (const track of tracks.get(prop) ?? []) {
    const v = constantValue(ctx, track);
    if (typeof v !== 'number') return null;
    sum += v;
  }
  return sum;
}

/** Whether a line's place on screen (world position, world rotation) stays the same over the whole chart. */
interface Frame {
  readonly pos: boolean;
  readonly rot: boolean;
}

const MOVING: Frame = Object.freeze({ pos: false, rot: false });

/**
 * The world frame of `line` as Phira composes parents (rendering.md R§3.1): the position is the parent's position
 * plus the line's own offset turned by the parent's whole rotation; the rotation adds the parent's only with "Also
 * turn with parent". So a line whose own position and rotation are constant still moves on screen when an ancestor
 * moves, or when an ancestor turns while the line sits off its parent's centre (or turns with it). Memoised per pass
 * in `memo`; a parent cycle (Phira refuses such a chart) counts as moving, so it never earns a "never moves" tip.
 */
function worldFrame(ctx: CheckContext, line: LineId, memo: Map<LineId, Frame>, visiting: Set<LineId>): Frame {
  const known = memo.get(line);
  if (known !== undefined) return known;
  const rec = ctx.doc.lines.get(line);
  if (rec === undefined) return MOVING;
  const tracks = ctx.idx.tracksOfLine(line);
  const x = constantSum(ctx, tracks, 'moveX');
  const y = constantSum(ctx, tracks, 'moveY');
  let pos = x !== null && y !== null;
  let rot = constantSum(ctx, tracks, 'rotate') !== null;
  const parent = rec.parent;
  if (parent !== null && ctx.doc.lines.has(parent)) {
    if (visiting.has(parent)) {
      pos = false;
      rot = false;
    } else {
      visiting.add(line);
      const up = worldFrame(ctx, parent, memo, visiting);
      visiting.delete(line);
      const centred = x !== null && y !== null && Math.abs(x) <= 1e-9 && Math.abs(y) <= 1e-9;
      if (!up.pos || (!up.rot && !centred)) pos = false;
      if (rec.rotateWithParent && !up.rot) rot = false;
    }
  }
  const frame: Frame = { pos, rot };
  memo.set(line, frame);
  return frame;
}

/** Properties that draw something of their own (text instead of the line image, paint strokes). */
const CONTENT_PROPS: readonly LineProp[] = ['text', 'paint'];
/** Properties that only move notes: a line without notes ignores them. */
const NOTE_ONLY_PROPS: ReadonlySet<LineProp> = new Set<LineProp>(['speed']);

type EmptyKind = 'invisible' | 'still';

/**
 * Why a line contributes nothing, or null when it does: it has no notes, no child lines, no image, HUD element,
 * video, text or paint, and either
 *  - 'invisible': its opacity never rises above 0 (no opacity track, as PH-10 reads it, or constant opacity layers
 *    that add up to 0 or less), so Phira draws nothing for it, moving or not; or
 *  - 'still': it is visible, no property changes over the whole chart (every track holds one value without a
 *    jump; scroll speed is ignored, it only moves notes) and no parent line carries it along (`worldFrame`): Phira
 *    draws the same plain line in the same place from start to end.
 * Lines made by the editor ("Add line": constant position, rotation, opacity and speed tracks) and imported RPE
 * lines (a default event layer per property) both carry tracks, so "has tracks" is never taken for "moves".
 */
function emptyKind(
  ctx: CheckContext,
  line: LineId,
  videoLines: ReadonlySet<LineId>,
  frames: Map<LineId, Frame>,
): EmptyKind | null {
  const rec = ctx.doc.lines.get(line);
  if (rec === undefined) return null;
  if (rec.attachUI !== null || rec.texture !== null || videoLines.has(line)) return null;
  if (ctx.idx.notesByLine(line).length > 0) return null;
  if (ctx.idx.childrenOf(line).length > 0) return null;
  const tracks = ctx.idx.tracksOfLine(line);
  for (const prop of CONTENT_PROPS) if ((tracks.get(prop) ?? []).length > 0) return null;

  let alpha: number | null = 0;
  for (const track of tracks.get('alpha') ?? []) {
    const v = constantValue(ctx, track);
    if (typeof v !== 'number') {
      alpha = null;
      break;
    }
    alpha += v;
  }
  if (alpha !== null && alpha <= 0) return 'invisible';

  for (const [prop, ids] of tracks) {
    if (NOTE_ONLY_PROPS.has(prop)) continue;
    for (const track of ids) if (constantValue(ctx, track) === undefined) return null;
  }
  if (rec.parent !== null) {
    const frame = worldFrame(ctx, line, frames, new Set());
    if (!frame.pos || !frame.rot) return null;
  }
  return 'still';
}

/** True when some line of the chart has a note (a chart without any is still being started: no "still" tips). */
function chartHasNotes(ctx: CheckContext): boolean {
  for (const line of ctx.doc.lineOrder) if (ctx.idx.notesByLine(line).length > 0) return true;
  return false;
}

const st04: CheckRule = {
  id: 'ST-04',
  category: 'style',
  severity: 'tip',
  level: 'guided',
  scope: 'chart',
  deps: ['lines', 'notes', 'motion', 'effects'],
  defaultEnabled: true,
  title: sm('st04Title'),
  check(ctx) {
    const videoLines = new Set<LineId>();
    for (const v of ctx.doc.videos.values()) if (v.attachLine !== null) videoLines.add(v.attachLine);
    const anyNotes = chartHasNotes(ctx);
    const frames = new Map<LineId, Frame>();
    const empty: Array<{ readonly line: LineId; readonly kind: EmptyKind }> = [];
    for (const line of linesToCheck(ctx)) {
      const kind = emptyKind(ctx, line, videoLines, frames);
      // A still, visible line is only worth a tip once the chart has notes elsewhere.
      if (kind === 'invisible' || (kind === 'still' && anyNotes)) empty.push({ line, kind });
    }
    const all = empty.map((e) => e.line);
    for (const { line, kind } of empty) {
      const name = label(ctx, line);
      const fixes: FixRef[] = [
        styleFixRef('problems.fix.deleteLines', sm('fixDeleteLine', { line: name }), { lines: [line] }),
      ];
      if (all.length > 1) {
        fixes.push(
          styleFixRef('problems.fix.deleteLines', sm('fixDeleteAllLines', { count: all.length }), {
            lines: [...all],
          }),
        );
      }
      fixes.push(revealFix(sm('fixShowLine'), Beats.ZERO, { line }));
      emitProblem(ctx, {
        id: problemId('ST-04', 'line', line),
        message: sm(kind === 'invisible' ? 'st04What' : 'st04WhatStill', { line: name }),
        why: sm(kind === 'invisible' ? 'st04Why' : 'st04WhyStill'),
        at: { line },
        fixes,
      });
    }
  },
};

// ---------------------------------------------------------------------------------------------------------------
// ST-05 long runs of straight segments
// ---------------------------------------------------------------------------------------------------------------

/**
 * ST-05's Full-level feature (asked in Guided only, `CheckRule.usesFeature`): motion that was not keyed
 * by hand in this editor's Guided tools, i.e. an imported RPE chart (lines kept as imported, keys that still carry
 * their RPE event data, as pasted RPE events do) or keys a generator wrote. Long straight runs are what baking and
 * those sources leave behind; a Guided user who keys a few straight segments by hand gets no tip about them.
 */
function usesBakedOrImportedMotion(ctx: CheckContext): boolean {
  if (hasImportedLines(ctx)) return true;
  for (const g of ctx.doc.generators.values()) if (g.outputs.keys.length > 0) return true;
  for (const k of ctx.doc.keys.values()) if (k.src !== undefined) return true;
  return false;
}

const st05: CheckRule = {
  id: 'ST-05',
  category: 'style',
  severity: 'tip',
  level: 'full',
  scope: 'line',
  deps: ['motion', 'tempo'],
  defaultEnabled: true,
  title: sm('st05Title'),
  usesFeature: usesBakedOrImportedMotion,
  check(ctx, only) {
    for (const line of linesToCheck(ctx, only)) {
      const byProp = ctx.idx.tracksOfLine(line);
      for (const prop of LINEAR_RUN_PROPS) {
        for (const track of byProp.get(prop) ?? []) {
          for (const run of linearRuns(keysOfTrack(ctx.doc, ctx.idx, track), LINEAR_RUN_MIN_SEGMENTS)) {
            const first = run.keys[0];
            const last = run.keys[run.keys.length - 1];
            if (first === undefined || last === undefined) continue;
            const keyIds = run.keys.map((k) => k.id);
            const redundant = redundantLinearKeys(run.keys, ctx.tempo);
            const fixes: FixRef[] = [];
            if (redundant.length > 0) {
              fixes.push(
                styleFixRef('problems.fix.removeRedundantKeys', sm('fixRemoveRedundant', { count: redundant.length }), {
                  keys: redundant,
                }),
              );
            }
            fixes.push(revealFix(sm('fixSelectKeys', { count: keyIds.length }), first.t, { line, keys: keyIds }));
            emitProblem(ctx, {
              id: problemId('ST-05', 'track', `${String(track)}-${String(first.id)}`),
              message: sm('st05What', {
                count: run.keys.length - 1,
                prop,
                line: label(ctx, line),
                from: fmtBeat(first.t),
                to: fmtBeat(last.t),
              }),
              why: sm('st05Why'),
              at: { line, beat: first.t, keys: keyIds, tracks: [track], field: 'segment.ease' },
              fixes,
            });
          }
        }
      }
    }
  },
};

// ---------------------------------------------------------------------------------------------------------------
// ST-06 RPE "Other" cautions (off by default)
// ---------------------------------------------------------------------------------------------------------------

type Custom = 'size' | 'fake' | 'visible';

const CUSTOM_KINDS: readonly {
  readonly kind: Custom;
  readonly what: string;
  readonly why: string;
  readonly field: string;
}[] = [
  { kind: 'size', what: 'st06WhatSize', why: 'st06WhySize', field: 'note.size' },
  { kind: 'fake', what: 'st06WhatFake', why: 'st06WhyFake', field: 'note.fake' },
  { kind: 'visible', what: 'st06WhatVisible', why: 'st06WhyVisible', field: 'note.visibleTime' },
];

function isCustom(n: Note, kind: Custom): boolean {
  switch (kind) {
    case 'size':
      return n.size !== 1;
    case 'fake':
      return n.fake;
    case 'visible':
      return n.visibleTime !== null;
  }
}

/**
 * ST-06's Full-level feature (asked in Guided only): the note fields only the Full level offers (DESIGN.md §2.4 "Note
 * fields": width and appear time). Decoration (fake) notes alone are a Guided field, so they do not turn it on.
 */
function usesFullNoteFields(ctx: CheckContext): boolean {
  for (const n of ctx.doc.notes.values()) if (isCustom(n, 'size') || isCustom(n, 'visible')) return true;
  return false;
}

const st06: CheckRule = {
  id: 'ST-06',
  category: 'style',
  severity: 'tip',
  level: 'full',
  scope: 'line',
  deps: ['notes'],
  defaultEnabled: false,
  title: sm('st06Title'),
  usesFeature: usesFullNoteFields,
  check(ctx, only) {
    for (const line of linesToCheck(ctx, only)) {
      const notes: Note[] = [];
      for (const id of ctx.idx.notesByLine(line)) {
        const n = ctx.doc.notes.get(id);
        if (n !== undefined) notes.push(n);
      }
      for (const c of CUSTOM_KINDS) {
        const hits = notes.filter((n) => isCustom(n, c.kind));
        const first = hits[0];
        if (first === undefined) continue;
        emitProblem(ctx, {
          id: problemId('ST-06', 'line', `${String(line)}-${c.kind}`),
          message: sm(c.what, { count: hits.length, line: label(ctx, line) }),
          why: sm(c.why),
          at: { line, beat: first.t, notes: hits.map((n) => n.id), field: c.field },
          fixes: [
            revealFix(sm('fixSelectNotes', { count: hits.length }), first.t, {
              line,
              notes: hits.map((n) => n.id),
            }),
          ],
        });
      }
    }
  },
};

// ---------------------------------------------------------------------------------------------------------------
// ST-07 / ST-08 MP3 and FLAC songs
// ---------------------------------------------------------------------------------------------------------------

function extOf(path: string): string {
  const i = path.lastIndexOf('.');
  return i < 0 ? '' : path.slice(i + 1).toLowerCase();
}

/** The song's container: the sniffed codec when known, else the mime type and file name. */
function songFormat(a: AssetRef): 'mp3' | 'flac' | 'other' {
  const codec = a.meta?.codec;
  if (codec !== undefined) return codec === 'mp3' ? 'mp3' : codec === 'flac' ? 'flac' : 'other';
  const mime = a.mime.toLowerCase();
  const ext = extOf(a.path);
  if (mime === 'audio/mpeg' || mime === 'audio/mp3' || ext === 'mp3') return 'mp3';
  if (mime === 'audio/flac' || mime === 'audio/x-flac' || ext === 'flac') return 'flac';
  return 'other';
}

function songRule(id: 'ST-07' | 'ST-08', format: 'mp3' | 'flac'): CheckRule {
  const what = id === 'ST-07' ? 'st07What' : 'st08What';
  const why = id === 'ST-07' ? 'st07Why' : 'st08Why';
  return {
    id,
    category: 'style',
    severity: 'tip',
    level: 'guided',
    scope: 'chart',
    deps: ['meta', 'assets'],
    defaultEnabled: true,
    title: sm(id === 'ST-07' ? 'st07Title' : 'st08Title'),
    check(ctx) {
      const music = ctx.doc.meta.music;
      if (music === null) return;
      const a = ctx.doc.assets.get(music);
      if (a === undefined || a.missing === true || songFormat(a) !== format) return;
      emitProblem(ctx, {
        id: problemId(id, 'asset', music),
        message: sm(what, { path: a.path }),
        why: sm(why),
        at: { asset: music, field: 'meta.music' },
        fixes: [
          fixRef('problems.fix.convertAudioToWav', sm('fixConvertToWav'), { asset: music }),
          navFix('assets.replace', sm('fixChooseSong'), { asset: music }),
        ],
      });
    },
  };
}

const st07 = songRule('ST-07', 'mp3');
const st08 = songRule('ST-08', 'flac');

// ---------------------------------------------------------------------------------------------------------------
// ST-09 incomplete chart details
// ---------------------------------------------------------------------------------------------------------------

const DETAIL_FIELDS: readonly (keyof ChartMeta & ('name' | 'composer' | 'charter' | 'illustrator' | 'level'))[] = [
  'name',
  'composer',
  'charter',
  'illustrator',
  'level',
];

const st09: CheckRule = {
  id: 'ST-09',
  category: 'style',
  severity: 'tip',
  level: 'guided',
  scope: 'chart',
  deps: ['meta'],
  defaultEnabled: true,
  title: sm('st09Title'),
  check(ctx) {
    const meta = ctx.doc.meta;
    for (const field of DETAIL_FIELDS) {
      const value = meta[field].trim();
      const state = value === '' ? 'empty' : value.toUpperCase() === UNKNOWN_DETAIL ? 'unknown' : null;
      if (state === null) continue;
      emitProblem(ctx, {
        id: problemId('ST-09', 'chart', field),
        message: sm('st09What', { field, state }),
        why: sm('st09Why'),
        at: { field: `meta.${field}` },
        // Focuses the field in Publish's PACKAGE panel (also when Publish is already open, UX-093).
        fixes: [navFix('inspector.focusField', sm('fixEditDetail', { field }), { field: `meta.${field}` })],
      });
    }
    const tags = new Set(meta.tags.map((t) => t.trim().toLowerCase()));
    if (!DIVISION_TAGS.some((t) => tags.has(t))) {
      emitProblem(ctx, {
        id: problemId('ST-09', 'chart', 'division'),
        message: sm('st09WhatDivision'),
        why: sm('st09WhyDivision'),
        at: { field: 'meta.tags' },
        fixes: [
          styleFixRef('problems.fix.addTag', sm('fixTagRegular'), { tag: 'regular' }),
          navFix('inspector.focusField', sm('fixEditTags'), { field: 'meta.tags' }),
        ],
      });
    }
  },
};

const rules: readonly CheckRule[] = [st01, st02, st03, st04, st05, st06, st07, st08, st09];

export default rules;
